import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { authApi } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import bgmain from "../../src/Images/main.jpg";
import Footer from "../Components/Footer";

export default function ProviderSignUp() {
  const [formData, setFormData] = useState({
    email: "",
    phoneNumber: "",
    password: "",
    companyName: "",
    confirmPassword: "",
  });
  // documents: Array<{ file: File, previewUrl?: string }>
  const [documents, setDocuments] = useState([]);
  // const [dragActive, setDragActive] = useState(false);
  // const [docError, setDocError] = useState("");
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const MAX_FILES = 5;
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB per backend
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  async function validateAndMergeFiles(incomingFiles) {
    // setDocError("");
    const currentCount = documents.length;
    const incoming = Array.from(incomingFiles || []);
    const remainingSlots = Math.max(0, MAX_FILES - currentCount);
    let acceptedLocal = [];
    let errors = [];
    for (const f of incoming) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        errors.push(`${f.name}: unsupported type`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        errors.push(`${f.name}: exceeds 5MB`);
        continue;
      }
      acceptedLocal.push({
        file: f,
        previewUrl: f.type.startsWith("image/")
          ? URL.createObjectURL(f)
          : undefined,
      });
      if (acceptedLocal.length >= remainingSlots) break;
    }
    // if (errors.length > 0) setDocError(errors[0]);
    // Pre-upload accepted files to backend and keep returned metadata
    if (acceptedLocal.length > 0) {
      const fd = new FormData();
      acceptedLocal.forEach((d) => fd.append("documents", d.file));
      try {
        const res = await authApi.providerPreUpload(fd);
        const returned = (res?.files || []).map((f, idx) => ({
          file: acceptedLocal[idx]?.file || null,
          previewUrl: acceptedLocal[idx]?.previewUrl,
          uploaded: {
            url: f.url,
            mimeType: f.mimeType,
            size: f.size,
            originalName: f.originalName,
          },
        }));
        setDocuments((prev) => [...prev, ...returned]);
      } catch (e) {
        // setDocError("Failed to upload files. Try again.");
      }
    }
  }

  // const handleFiles = (e) => {
  //   validateAndMergeFiles(e.target.files);
  //   // reset input so selecting the same file again re-triggers change
  //   e.target.value = "";
  // };

  // const handleDragOver = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setDragActive(true);
  // };

  // const handleDragLeave = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setDragActive(false);
  // };

  // const handleDrop = (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setDragActive(false);
  //   if (e.dataTransfer?.files?.length) {
  //     validateAndMergeFiles(e.dataTransfer.files);
  //   }
  // };

  // useEffect(() => {
  //   return () => {
  //     documents.forEach(
  //       (d) => d.previewUrl && URL.revokeObjectURL(d.previewUrl)
  //     );
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError("");
    if (!formData.phoneNumber.startsWith("+")) {
      alert("Please enter a valid mobile number.");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("email", formData.email);
      fd.append("password", formData.password);
      fd.append("companyName", formData.companyName);
      fd.append("phoneNumber", formData.phoneNumber);
      // send uploaded docs metadata instead of raw files
      const docsMeta = documents
        .filter((d) => d.uploaded)
        .map((d) => d.uploaded);
      if (docsMeta.length > 0)
        fd.append("documentsMeta", JSON.stringify(docsMeta));
      const data = await authApi.providerRegister(fd);
      // Reuse login helper to set tokens and user
      login(data);
      navigate("/listing");
    } catch (error) {
      const status = error?.status;
      const data = error?.data || {};
      if (data?.code === "VALIDATION_ERROR") {
        const first =
          Array.isArray(data.errors) && data.errors.length > 0
            ? data.errors[0]?.message
            : null;
        setGeneralError(first || data?.message || "Validation error");
      } else if (status === 409 || data?.code === "CONFLICT") {
        setGeneralError(data?.message || "Email already exists");
      } else {
        setGeneralError(data?.message || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="min-h-screen min-w-full flex items-center fl">
        <img
          src={bgmain}
          alt="Hero Background"
          className="absolute min-h-screen inset-0 w-full h-full object-cover z-[-10] blur-sm"
        />
        <div className="w-11/12 md:w-2/5 mx-auto px-6 py-10 bg-gray-100/30 rounded-lg backdrop-blur-sm poppins-regular">
          <h2 className="text-3xl poppins-bold uppercase text-center mb-8 text-gray-800">
            Business Sign Up
          </h2>
          {generalError && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
              {generalError}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
                Company Name
              </label>
              <input
                type="text"
                name="companyName"
                required
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
                Mobile Number
              </label>
              <PhoneInput
                country={"in"}
                value={formData.phoneNumber}
                onChange={(v) =>
                  setFormData((p) => ({ ...p, phoneNumber: `+${v}` }))
                }
                inputStyle={{
                  width: "100%",
                  height: "42px",
                  fontSize: "16px",
                  borderRadius: "0.5rem",
                  border: "1px solid #d1d5db",
                }}
                buttonStyle={{
                  borderRadius: "0.5rem 0 0 0.5rem",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
            {/* <div>
          <label className="block text-sm font-semibold uppercase text-gray-900 mb-1">
            Documents (optional)
          </label>
          <div
            className={`w-full border-2 border-dashed rounded-lg p-4 text-center ${
              dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <p className="text-gray-900 mb-2">Drag and drop files here, or</p>
            <label className="inline-block px-4 py-2 bg-blue-600 text-white rounded cursor-pointer">
              Browse Files
              <input
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                multiple
                onChange={handleFiles}
                className="hidden"
              />
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Up to {MAX_FILES} files. Allowed: JPG, PNG, PDF. Max 5MB each.
            </p>
          </div>
          {docError && (
            <div className="mt-2 text-sm text-red-600">{docError}</div>
          )}

          {documents.length > 0 && (
            <ul className="mt-3 grid grid-cols-1 gap-2">
              {documents.map((d, idx) => {
                const displayName =
                  d.file?.name || d.uploaded?.originalName || "Document";
                const bytes =
                  d.file && typeof d.file.size === "number"
                    ? d.file.size
                    : d.uploaded?.size || 0;
                const sizeMb = (bytes / 1024 / 1024).toFixed(2);
                return (
                  <li
                    key={idx}
                    className="flex items-center justify-between border rounded p-2 bg-white"
                  >
                    <div className="flex items-center gap-3">
                      {d.previewUrl ? (
                        // image preview
                        <img
                          src={d.previewUrl}
                          alt={displayName}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <span className=" w-12 h-12 flex items-center justify-center rounded bg-gray-100 text-gray-900 text-xs">
                          PDF
                        </span>
                      )}
                      <div className="text-sm">
                        <div className="font-medium text-gray-800 truncate max-w-[220px]">
                          {displayName}
                        </div>
                        <div className="text-gray-500">{sizeMb} MB</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDocuments((prev) => {
                          const next = [...prev];
                          const removed = next.splice(idx, 1)[0];
                          if (removed?.previewUrl)
                            URL.revokeObjectURL(removed.previewUrl);
                          return next;
                        });
                      }}
                      className="px-2 py-1 text-sm bg-red-100 text-red-700 rounded"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div> */}
            <div className="text-right">
              <button
                type="submit"
                disabled={loading}
                className={`bg-gradient-to-r from-blue-500 to-blue-700 uppercase px-6 py-2 text-white rounded-lg mulish-bold transition ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:text-blue-200"
                }`}
              >
                {loading ? "Submitting..." : "Register"}
              </button>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </>
  );
}
